import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Bot, Shield, Zap, Loader2 } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const PACKAGES = {
  starter: {
    name: 'Starter',
    setupFee: 150,
    monthlyPrice: 46,
    description: 'Perfect for small practices getting started with AI assistance.',
  },
  professional: {
    name: 'Professional',
    setupFee: 230,
    monthlyPrice: 86.25,
    description: 'For growing firms managing multiple websites or higher volumes.',
  },
};

const AssistantsGetAssistant: React.FC = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<'starter' | 'professional'>('starter');
  const [formData, setFormData] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    industry: 'accounting',
    useCase: '',
  });

  const handleCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.businessName || !formData.contactName || !formData.email || !formData.useCase) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in all required fields.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('create-assistant-checkout', {
        body: {
          packageType: selectedPackage,
          industry: formData.industry,
          businessName: formData.businessName,
          contactName: formData.contactName,
          email: formData.email,
          phone: formData.phone,
          useCase: formData.useCase,
          successUrl: `${window.location.origin}/assistants/checkout-success`,
          cancelUrl: `${window.location.origin}/assistants/get-assistant`,
        },
      });

      if (error) throw error;

      if (data?.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast({
        title: 'Checkout Failed',
        description: 'There was an error starting checkout. Please try again or contact us.',
        variant: 'destructive',
      });
      setIsSubmitting(false);
    }
  };

  const features = [
    { icon: Bot, text: 'Custom-trained AI assistant' },
    { icon: Shield, text: 'Governance framework included' },
    { icon: Zap, text: 'Live in as little as 14 days' },
  ];

  const processSteps = [
    { step: '1', title: 'Complete Checkout', description: 'Secure payment via Stripe' },
    { step: '2', title: 'Onboarding Wizard', description: 'Upload FAQs, docs & brand assets' },
    { step: '3', title: 'Build & Test', description: 'We configure your assistant' },
    { step: '4', title: 'Review & Approve', description: 'Preview before going live' },
    { step: '5', title: 'Go Live', description: 'Deploy to your website' },
  ];

  const pkg = PACKAGES[selectedPackage];

  return (
    <>
      <Navbar />
      <main className="pt-16">
        {/* Hero */}
        <section className="bg-gradient-to-br from-brand-navy to-brand-blue py-16 lg:py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-space">
                Get Your Advisory Assistant
              </h1>
              <p className="text-xl text-white/90 mb-8">
                Choose your package, complete checkout, and start your onboarding journey.
              </p>
              <div className="flex flex-wrap justify-center gap-6">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 text-white/90">
                    <feature.icon className="h-5 w-5 text-brand-teal" />
                    <span>{feature.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Package Selection + Form */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
              {/* Form */}
              <div>
                <h2 className="text-2xl font-bold mb-6 text-foreground">1. Choose Your Package</h2>
                
                {/* Package Selection */}
                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  {(Object.entries(PACKAGES) as [keyof typeof PACKAGES, typeof PACKAGES.starter][]).map(([key, pkgOption]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setSelectedPackage(key)}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        selectedPackage === key
                          ? 'border-brand-teal bg-brand-teal/5'
                          : 'border-border hover:border-brand-teal/50'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-card-foreground">{pkgOption.name}</span>
                        {selectedPackage === key && (
                          <CheckCircle className="h-5 w-5 text-brand-teal" />
                        )}
                      </div>
                      <div className="text-2xl font-bold text-card-foreground">
                        ${pkgOption.monthlyPrice}<span className="text-sm font-normal text-muted-foreground">/mo</span>
                      </div>
                      <div className="text-sm text-brand-teal font-medium">
                        + ${pkgOption.setupFee.toLocaleString()} setup
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">{pkgOption.description}</div>
                    </button>
                  ))}
                </div>

                <h2 className="text-2xl font-bold mb-6 text-foreground">2. Your Details</h2>
                <form onSubmit={handleCheckout} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="businessName">Business Name *</Label>
                      <Input
                        id="businessName"
                        required
                        value={formData.businessName}
                        onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                        placeholder="Your company name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contactName">Contact Name *</Label>
                      <Input
                        id="contactName"
                        required
                        value={formData.contactName}
                        onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                        placeholder="Your name"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="you@company.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+64 21 000 0000"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="industry">Industry *</Label>
                    <select
                      id="industry"
                      required
                      value={formData.industry}
                      onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <option value="accounting">Accounting</option>
                      <option value="legal">Legal</option>
                      <option value="financial-services">Financial Services</option>
                      <option value="professional-services">Professional Services</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="useCase">What would you like your assistant to help with? *</Label>
                    <Textarea
                      id="useCase"
                      required
                      value={formData.useCase}
                      onChange={(e) => setFormData({ ...formData, useCase: e.target.value })}
                      placeholder="E.g., Answer client questions about software, guide onboarding, handle FAQ..."
                      rows={4}
                    />
                  </div>

                  {/* Price Summary */}
                  <div className="bg-muted/50 rounded-xl p-6">
                    <h3 className="font-semibold mb-4 text-foreground">Order Summary</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{pkg.name} Monthly Subscription</span>
                        <span className="text-card-foreground">${pkg.monthlyPrice}/mo</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">One-time Setup Fee</span>
                        <span className="text-card-foreground">${pkg.setupFee.toLocaleString()}</span>
                      </div>
                      <div className="border-t border-border pt-2 mt-2">
                        <div className="flex justify-between font-semibold">
                          <span className="text-foreground">Due Today</span>
                          <span className="text-foreground">${(pkg.monthlyPrice + pkg.setupFee).toLocaleString()} NZD</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Then ${pkg.monthlyPrice}/month starting next billing cycle
                        </p>
                      </div>
                    </div>
                  </div>

                  <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Redirecting to Checkout...
                      </>
                    ) : (
                      <>
                        Proceed to Checkout
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>
                  <p className="text-sm text-muted-foreground text-center">
                    Secure payment powered by Stripe. Cancel anytime.
                  </p>
                </form>
              </div>

              {/* Process */}
              <div>
                <h2 className="text-2xl font-bold mb-6 text-foreground">How It Works</h2>
                <div className="space-y-6">
                  {processSteps.map((step, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 bg-brand-teal/10 rounded-full flex items-center justify-center">
                        <span className="text-brand-teal font-bold">{step.step}</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-card-foreground">{step.title}</h3>
                        <p className="text-sm text-muted-foreground">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 p-6 bg-muted/50 rounded-xl">
                  <h3 className="font-semibold mb-3 text-foreground">Included in Every Package</h3>
                  <ul className="space-y-2">
                    {[
                      'Governed AI assistant with QA testing',
                      'Deployed across all pages of your website',
                      'Industry-specific governance pack',
                      'Website chat widget embedding',
                      'Monthly usage and governance reports',
                      'Ongoing support and updates',
                    ].map((item, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="h-4 w-4 text-brand-teal" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-6 p-6 bg-brand-teal/5 border border-brand-teal/20 rounded-xl">
                  <h3 className="font-semibold mb-2 text-foreground">🔐 After Checkout</h3>
                  <p className="text-sm text-muted-foreground">
                    You'll receive a secure magic link email (no password required!) that takes you 
                    directly to your onboarding dashboard. Check your inbox and spam folder.
                  </p>
                </div>

                <div className="mt-6 text-center">
                  <Link to="/assistants/pricing" className="text-brand-teal hover:underline text-sm">
                    Compare all packages →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default AssistantsGetAssistant;
